import type { Crop } from './Crop';
import { adaptCrop } from './Crop';
import type { BbchInfo, BbchRange } from '../types/simulation';

export type PlantingLifecycleStatus = 'planned' | 'active' | 'holding' | 'transferred' | 'sold' | 'dead' | 'archived';
export type PlantingType = 'single' | 'batch';
export type PlantingOriginType = 'seed' | 'cutting' | 'seedling' | 'division' | 'graft' | 'layering' | 'bulb' | 'transplant_in' | 'unknown';

export interface PlantingPhoto {
  id: number | string;
  plantingId: number | string;
  url: string;
  thumbnailUrl?: string;
  fileName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  uploadedBy?: string;
  description?: string;
  metadata?: {
    location?: { latitude: number; longitude: number };
    tags?: string[];
  };
}

export interface PlantingLocation {
  id: number | string;
  bedId: number | string | null;
  saveStorageId?: number | string | null;
  fromDate?: string | null;
  toDate?: string | null;
  bed?: {
    id: number | string;
    name?: string | null;
    plot_id?: number | string | null;
    anchor_row_world?: number | null;
    anchor_col_world?: number | null;
    anchor_code?: string | null;
  } | null;
  plot?: {
    id: number | string;
    field_id?: number | string | null;
    name?: string | null;
  } | null;
  field?: {
    id: number | string;
    farm_id?: number | string | null;
    name?: string | null;
  } | null;
  farmStorage?: {
    id: number | string;
    farm_id?: number | string | null;
    name?: string | null;
  } | null;
  farm?: {
    id: number | string;
    name?: string | null;
  } | null;
}

export interface LocationContext {
  farmsTotal: number;
  fieldsTotal: number;
  plotsTotal: number;
}

export interface CurrentProperty {
  propertyId: string | number;
  propertyCode: string;
  propertyName: string;
  dataType: string;
  valueText?: string | null;
  valueNumber?: number | null;
  valueBoolean?: boolean | null;
  unit?: string | null;
  updatedAt?: string | null;
}

export interface Planting {
  id: number | string;
  uuid?: string;
  storeId?: string;
  publicId: string;
  crop: Crop;
  plantingType?: PlantingType; // Тип посадки: одиночная или групповая
  quantity?: number; // Количество растений в группе (по умолчанию 1)
  initialQuantity?: number | null; // Исходное количество до списаний/сборов
  createdAt?: string | null;
  createdBy?: string | null;
  lifecycleStatus?: PlantingLifecycleStatus;
  statusChangedAt?: string | null;
  statusChangedBy?: string | null;
  statusReason?: string | null;
  originDate?: string | null;
  originType?: PlantingOriginType;
  autoHarvest?: boolean;
  locations: PlantingLocation[];
  locationContext?: LocationContext;
  photos?: PlantingPhoto[];
  currentProperties?: CurrentProperty[];
  bbch?: BbchInfo | null;
  inStorage?: boolean; // Посадка находится в хранилище (нет активной грядки)
}

export interface PlantingQueryParams {
  saveId: string;
  fieldId?: number | string;
  plotId?: number | string;
  bedId?: number | string;
  cropId?: number | string;
  simSessionId?: string;
  lifecycleStatus?: PlantingLifecycleStatus;
  limit?: number;
  offset?: number;
}

export type PlantingLocationInput = {
  id?: number | string | null;
  bedId: number | string;
  fromDate: string;
  toDate?: string | null;
};

export type PlantingPayload = {
  cropId: number | string;
  plantingType: PlantingType; // Обязательное поле: "single" или "batch"
  locations: PlantingLocationInput[];
  quantity?: number; // Количество растений в группе (для batch)
  lifecycleStatus?: PlantingLifecycleStatus;
  statusReason?: string | null;
  originDate?: string | null;
  originType?: PlantingOriginType;
  autoHarvest?: boolean;
};

export type PlantingUpdatePayload = Partial<PlantingPayload>;

export const adaptPlantingLocation = (data: any): PlantingLocation => ({
  id: data.id,
  bedId: data.bedId ?? data.bed_id,
  saveStorageId: data.saveStorageId ?? data.save_storage_id ?? null,
  fromDate: data.fromDate ?? data.from_date ?? null,
  toDate: data.toDate ?? data.to_date ?? null,
  bed: data.bed ?? null,
  plot: data.plot ?? null,
  field: data.field ?? null,
  farmStorage: data.farmStorage ?? data.farm_storage ?? null,
  farm: data.farm ?? null,
});

const toFiniteNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toStringOrNull = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

const firstDefined = (...values: unknown[]): unknown => (
  values.find((value) => value !== null && value !== undefined && value !== '')
);

const parseBbchRange = (value: unknown): BbchRange | null => {
  const raw = typeof value === 'string'
    ? (() => {
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      })()
    : value;
  if (!raw || typeof raw !== 'object') return null;
  const from = toFiniteNumberOrNull((raw as any).from);
  const to = toFiniteNumberOrNull((raw as any).to);
  return from != null && to != null ? { from, to } : null;
};

const getPropertyValue = (properties: CurrentProperty[] | undefined, codes: string[]): unknown => {
  if (!Array.isArray(properties)) return undefined;
  const normalizedCodes = new Set(codes.map((code) => code.toLowerCase()));
  const property = properties.find((item) => normalizedCodes.has(String(item.propertyCode || '').toLowerCase()));
  if (!property) return undefined;
  return firstDefined(property.valueNumber, property.valueText, property.valueBoolean);
};

export const adaptBbchInfo = (data: any, properties?: CurrentProperty[]): BbchInfo | null => {
  const source = data?.bbch && typeof data.bbch === 'object'
    ? data.bbch
    : data?.lifecycle?.bbch && typeof data.lifecycle.bbch === 'object'
      ? data.lifecycle.bbch
      : data;
  if (!source || typeof source !== 'object') return null;

  const code = toFiniteNumberOrNull(firstDefined(
    source.code,
    source.bbchCode,
    source.bbch_code,
    getPropertyValue(properties, ['bbch_code', 'bbchCode'])
  ));
  const status = toStringOrNull(firstDefined(
    source.status,
    source.bbchStatus,
    source.bbch_status,
    getPropertyValue(properties, ['bbch_status', 'bbchStatus'])
  ));
  const label = toStringOrNull(firstDefined(
    source.label,
    source.bbchLabel,
    source.bbch_label,
    getPropertyValue(properties, ['bbch_label', 'bbchLabel'])
  ));
  const principalStage = toFiniteNumberOrNull(firstDefined(
    source.principalStage,
    source.bbchPrincipalStage,
    source.bbch_principal_stage,
    getPropertyValue(properties, ['bbch_principal_stage', 'bbchPrincipalStage'])
  ));
  const range = parseBbchRange(firstDefined(
    source.range,
    source.bbchRange,
    source.bbch_range,
    getPropertyValue(properties, ['bbch_range', 'bbchRange'])
  ));

  if (code == null && !status && !label && principalStage == null && !range) {
    return null;
  }

  return {
    code,
    codeText: toStringOrNull(firstDefined(
      source.codeText,
      source.bbchCodeText,
      source.bbch_code_text,
      getPropertyValue(properties, ['bbch_code_text', 'bbchCodeText'])
    )) ?? (code != null ? `BBCH ${code}` : null),
    principalStage,
    principalLabel: toStringOrNull(firstDefined(
      source.principalLabel,
      source.bbchPrincipalLabel,
      source.bbch_principal_label,
      getPropertyValue(properties, ['bbch_principal_label', 'bbchPrincipalLabel'])
    )),
    label,
    confidence: toStringOrNull(firstDefined(
      source.confidence,
      source.bbchConfidence,
      source.bbch_confidence,
      getPropertyValue(properties, ['bbch_confidence', 'bbchConfidence'])
    )) as BbchInfo['confidence'],
    cropGroup: toStringOrNull(firstDefined(
      source.cropGroup,
      source.bbchCropGroup,
      source.bbch_crop_group,
      getPropertyValue(properties, ['bbch_crop_group', 'bbchCropGroup'])
    )),
    scale: toStringOrNull(firstDefined(
      source.scale,
      source.bbchScale,
      source.bbch_scale,
      getPropertyValue(properties, ['bbch_scale', 'bbchScale'])
    )),
    scaleVersion: toStringOrNull(firstDefined(
      source.scaleVersion,
      source.bbchScaleVersion,
      source.bbch_scale_version,
      getPropertyValue(properties, ['bbch_scale_version', 'bbchScaleVersion'])
    )),
    range,
    status: status as BbchInfo['status'],
  };
};

export const adaptPlanting = (data: any): Planting => {
  const rawId = data.id ?? '';
  const rawIdStr = String(rawId);
  const isUuid = rawIdStr.includes('-');
  const uuid = data.uuid ?? (isUuid ? rawIdStr : undefined);
  const storeId = data.storeId ?? data.store_id ?? (!isUuid ? rawIdStr : undefined);
  const currentProperties: CurrentProperty[] = Array.isArray(data.currentProperties ?? data.current_properties)
    ? (data.currentProperties ?? data.current_properties).map((prop: any) => ({
        propertyId: prop.propertyId ?? prop.property_id,
        propertyCode: prop.propertyCode ?? prop.property_code,
        propertyName: prop.propertyName ?? prop.property_name,
        dataType: String(prop.dataType ?? prop.data_type ?? ''),
        valueText: prop.valueText ?? prop.value_text ?? null,
        valueNumber: prop.valueNumber ?? prop.value_number ?? null,
        valueBoolean: prop.valueBoolean ?? prop.value_boolean ?? null,
        unit: prop.unit ?? null,
        updatedAt: prop.updatedAt ?? prop.updated_at ?? null,
      }))
    : [];

  return {
    id: uuid ?? rawId,
    uuid,
    storeId,
    publicId: data.publicId ?? data.public_id ?? storeId ?? String(rawId ?? ''),
  crop: data.crop
    ? adaptCrop(data.crop)
    : adaptCrop({
        id: data.crop_id,
        name: data.crop_name ?? '',
        latin_name: data.crop_latin_name ?? '',
      }),
  plantingType: data.plantingType ?? data.planting_type ?? (data.quantity > 1 ? 'batch' : 'single'),
  quantity: data.quantity ?? 1, // По умолчанию 1 (одно растение)
  initialQuantity: data.initialQuantity ?? data.initial_quantity ?? null,
  createdAt: data.createdAt ?? data.created_at ?? null,
  createdBy: data.createdBy ?? data.created_by ?? null,
  originDate: data.originDate ?? data.origin_date ?? null,
  originType: data.originType ?? data.origin_type ?? 'unknown',
  autoHarvest: data.autoHarvest ?? data.auto_harvest_enabled ?? false,
  lifecycleStatus: data.lifecycleStatus ?? data.lifecycle_status ?? 'active',
  statusChangedAt: data.statusChangedAt ?? data.status_changed_at ?? null,
  statusChangedBy: data.statusChangedBy ?? data.status_changed_by ?? null,
  statusReason: data.statusReason ?? data.status_reason ?? null,
  locations: Array.isArray(data.locations) ? data.locations.map(adaptPlantingLocation) : [],
  locationContext: data.locationContext ?? data.location_context ?? undefined,
    currentProperties,
    bbch: adaptBbchInfo(data, currentProperties),
    photos: Array.isArray(data.photos) ? data.photos.map((p: any) => ({
    id: p.id,
    plantingId: p.plantingId ?? p.planting_id,
    url: p.url,
    thumbnailUrl: p.thumbnailUrl ?? p.thumbnail_url,
    fileName: p.fileName ?? p.file_name,
    mimeType: p.mimeType ?? p.mime_type,
    size: p.size,
    uploadedAt: p.uploadedAt ?? p.uploaded_at,
    uploadedBy: p.uploadedBy ?? p.uploaded_by,
    description: p.description,
    metadata: p.metadata,
    })) : [],
  inStorage: data.inStorage ?? data.in_storage ?? false,
  };
};
